import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Currency } from '@/utils/currencyConverter';

interface ExchangeRates {
  rates: Record<Currency, number>;
  lastUpdated: Date | null;
  loading: boolean;
}

// Default fallback rates if database fetch fails
const FALLBACK_RATES: Record<Currency, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.52,
};

export const useExchangeRates = (): ExchangeRates => {
  const [rates, setRates] = useState<Record<Currency, number>>(FALLBACK_RATES);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const { data, error } = await supabase
          .from('exchange_rates')
          .select('target_currency, rate, fetched_at')
          .eq('base_currency', 'USD');

        if (error) {
          console.error('Error fetching exchange rates:', error);
          return;
        }

        if (data && data.length > 0) {
          const rateMap: Record<Currency, number> = { USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.52 };
          data.forEach(row => {
            if (['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(row.target_currency)) {
              rateMap[row.target_currency as Currency] = Number(row.rate);
            }
          });
          setRates(rateMap);
          
          // Get most recent update time
          const mostRecent = data.reduce((latest, row) => {
            const rowDate = new Date(row.fetched_at);
            return rowDate > latest ? rowDate : latest;
          }, new Date(0));
          setLastUpdated(mostRecent);
        }
      } catch (err) {
        console.error('Failed to fetch exchange rates:', err);
      } finally {
        setLoading(false);
      }
    };

    // Defer API call by 2 seconds to avoid blocking initial boot
    const timer = setTimeout(() => {
      fetchRates();
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  return { rates, lastUpdated, loading };
};
